import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import contractorActions from "../../../../_actions/contractorActions";
import { CONTRACTOR } from "../../../../_constants/actionConstants";
import projActions from "../../../../_actions/projActions";
import MultiselectTableV2 from "../../../_common/MultiselectTableV2";
import EditContractorModalV2 from "../../../AdminTasks/AdminContractors/EditContractorModalV2";
import AlertModal from "../../../_common/AlertModal";
import AlertBox from "../../../_common/AlertBox";
import ContractorProjectsList from "./ContractorProjectsList";

export default function EditProjContractorsModalV2(props) {
  const { showEditPcModal, projectId, tabId, toggleEditPcModal } = props;
  const dispatch = useDispatch();

  const columns = useRef([
    {
      path: "name",
      label: "Name",
      isSortable: false,
      isSearchable: true,
      width: "70%",
      content: (item) => `${item.name} (${item.projectCount ?? 0})`,
    },
    {
      path: "abbreviation",
      label: "Abbrev.",
      isSortable: false,
      isSearchable: true,
      width: "30%",
    },
  ]);

  const [showAlert_hasLinks, setShowAlert_hasLinks] = useState(false);
  const toggleAlert_hasLinks = () => setShowAlert_hasLinks(!showAlert_hasLinks);

  // Expandable rows state
  const [expandedContractorIds, setExpandedContractorIds] = useState([]);
  const [pendingDetailsRequest, setPendingDetailsRequest] = useState(null);
  
  const openContractorDetails = useSelector(
    (state) => state.contractors.openContractorDetails || {}
  );

  // Fetch contractor details when a pending request is made
  useEffect(() => {
    if (pendingDetailsRequest !== null) {
      if (!openContractorDetails[pendingDetailsRequest]) {
        dispatch(contractorActions.getDetails(pendingDetailsRequest));
      }
      setPendingDetailsRequest(null);
    }
  }, [pendingDetailsRequest, openContractorDetails, dispatch]);

  const handleToggleExpand = useCallback(
    (contractorId) => {
      setExpandedContractorIds((prev) => {
        if (prev.includes(contractorId)) {
          return prev.filter((id) => id !== contractorId);
        } else {
          // Request details fetch in separate effect
          setPendingDetailsRequest(contractorId);
          return [...prev, contractorId];
        }
      });
    },
    []
  );

  const renderExpandedContent = useCallback(
    (item) => {
      const contractorDetails = openContractorDetails[item.contractorId];
      return (
        <ContractorProjectsList
          contractorId={item.contractorId}
          projects={contractorDetails?.projects}
          loading={!contractorDetails}
          error={contractorDetails?.error === true}
        />
      );
    },
    [openContractorDetails]
  );

  // Reference to redux store's contractorList (may be null when
  // component mounts - it will be fetched/refreshed when MultiselectTableV2
  // calls getPaged(), which is defined and provided via props from this
  // component.)
  const contractorList = useSelector(
    (state) => state.contractors.contractorList[tabId] || null
  );
  const getPagedContractorList = useCallback(
    async (searchString, currPage, pageSize, refreshList) =>
      await dispatch(
        contractorActions.getPaged(tabId, currPage, pageSize, searchString, refreshList)
      ),
    [tabId, dispatch]
  );

  // Reference to redux store's projContractorList
  const projContractorList = useSelector(
    (state) => state.contractors.projContractorLists[projectId] || null
  );

  useEffect(() => {
    !projContractorList && dispatch(contractorActions.getAllForProject(projectId));

    return () => {
      dispatch({ type: CONTRACTOR.CLEAR_MISC_SESSION_DATA, tabId });
      dispatch({ type: CONTRACTOR.CLEAR_LIST, tabId });
    };
  }, [projectId, tabId, projContractorList, dispatch]);

  // Derived state to manage contractor selections within current modal session.
  // This controls selectedContractors displayed in the right panel.
  // An array of contractorIds.
  const [contractorIds, setContractorIds] = useState(
    (projContractorList || []).map((pc) => pc.contractorId)
  );
  
  // Update selectedContractors for the right panel.
  // Build selectedContractors from scratch whenever contractorIds or lists are updated.
  // contractorIds is updated whenever a contractor is selected/deselected.
  const [selectedContractors, setSelectedContractors] = useState(
    _.orderBy(projContractorList || [], "name", "asc")
  );

  const [clientDeselectedContractorId, setClientDeselectedContractorId] = useState(null);

  // Sync contractorIds with projContractorList
  useEffect(() => {
    setContractorIds((projContractorList || []).map((pc) => pc.contractorId));
  }, [projContractorList]);

  // Sync selectedContractors with contractorIds
  useEffect(() => {
    let _selectedContractors = []; // Blank array to rebuild selectedLocations

    // Loop thru incoming contractorIds to apply changes. Unshift (not push) new item.
    contractorIds.forEach((cid) => {
      const existPc = (projContractorList || []).find((pc) => pc.contractorId === cid);

      // If a selection exists, add the existing
      if (existPc) {
        // Check to ensure it's unique (do not allow duplicates)
        if (!(_selectedContractors || []).some((pc) => pc.contractorId === cid))
          _selectedContractors.push(existPc);
      }
      // If cid is not found in current selectedContractors, then create new
      else {
        const ctrToAdd = (contractorList || []).find((c) => c.contractorId === cid);
        if (ctrToAdd) {
          const newPc = {
            contractorId: ctrToAdd.contractorId,
            name: ctrToAdd.name,
            abbreviation: ctrToAdd.abbreviation,
            projectAbbreviation: ctrToAdd.abbreviation, // copy over from ctr
            isPrime: false,
            phone: "",
            address: "",
            isDeleted: false,
          };
          _selectedContractors = [newPc, ..._selectedContractors];
        }
      }
    });

    _selectedContractors = _.orderBy(_selectedContractors, "name", "asc");
    setSelectedContractors(_selectedContractors);
  }, [contractorIds, projContractorList, contractorList]);

  const [modalState_addNewContractor, setModalState_addNewContractor] = useState({
    showModal: false,
    data: null,
  });
  const toggleModal_addNewContractor = () =>
    setModalState_addNewContractor({
      ...modalState_addNewContractor,
      showModal: !modalState_addNewContractor.showModal,
    });

  const dispatchSearchWaitingFor = useCallback((searchString) => {
    dispatch({
      type: CONTRACTOR.SET_SEARCH_WAITINGFOR,
      tabId,
      waitingFor: searchString,
    });
  }, [tabId, dispatch]);

  const updateIsPrime = (rowIndex, value) => {
    const pcList = selectedContractors.map((pc) => ({ ...pc }));
    pcList[rowIndex].isPrime = value;
    pcList.forEach((pc, index) => {
      if (pc.isPrime && index !== rowIndex) pc.isPrime = false;
    });

    setSelectedContractors(pcList);
  };

  const unassignContractor = async (contractorId) => {
    const hasLinks = await dispatch(
      projActions.contractorHasLinks(projectId, contractorId)
    );
    if (!hasLinks) setClientDeselectedContractorId(contractorId);
    else toggleAlert_hasLinks();
  };

  const handleSubmit_pcForm = async (e) => {
    e.preventDefault();
    await dispatch(
      contractorActions.updateProjectContractorSelection(projectId, selectedContractors)
    );
    toggleEditPcModal();
  };

  const closeBtn = (
    <button className="close" onClick={toggleEditPcModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <React.Fragment>
      <Modal isOpen={showEditPcModal} toggle={toggleEditPcModal} size={"xl"} fade={false}>
        <ModalHeader
          toggle={toggleEditPcModal}
          className="bg-info text-white"
          close={closeBtn}
        >
          Edit Project Contractors
        </ModalHeader>
        <ModalBody>
          <AlertBox color="info" icon="info-circle" title="Adding Contractor(s):">
            First, start by searching. If the contractor you're looking for is not on the
            list, select "Add..." link at the bottom of the list.
          </AlertBox>
          <div className="d-flex flex-row">
            <ProjContractorMultiselect_Left>
              <MultiselectTableV2
                className="table-pc-contractor-list flex-grow-1"
                data={contractorList || []}
                idPropName="contractorId"
                columns={columns.current}
                getPaged={getPagedContractorList}
                dispatchSearchWaitingFor={dispatchSearchWaitingFor}
                updateSelectedItemIds={setContractorIds}
                selectedItemIds={contractorIds}
                disabledItemIds={[]}
                clientDeselectedItemId={clientDeselectedContractorId}
                setClientDeselectedItemId={setClientDeselectedContractorId}
                getDeselectAllowed={async (contractorId) => {
                  const hasLinks = await dispatch(
                    projActions.contractorHasLinks(projectId, contractorId)
                  );
                  return !hasLinks;
                }}
                toggleDeselectNotAllowedAlert={toggleAlert_hasLinks}
                deselectCallback={null}
                searchType="paginated"
                searchPlaceholder="Search..."
                showAdd={(contractorList || []).length < 5}
                addCallback={(searchString) =>
                  setModalState_addNewContractor({
                    data: { isNew: true, searchString },
                    showModal: true,
                  })
                }
                expandable={true}
                expandedItemIds={expandedContractorIds}
                onToggleExpand={handleToggleExpand}
                renderExpandedContent={renderExpandedContent}
              />
            </ProjContractorMultiselect_Left>
            <ProjContractorMultiselect_Right
              selectedContractors={selectedContractors}
              updateIsPrime={updateIsPrime}
              unassignContractor={unassignContractor}
            />
          </div>
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          <div className="form-row form-group mb-0">
            <button
              className="btn btn-secondary mr-2"
              color="secondary"
              onClick={toggleEditPcModal}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              id="savePcButton"
              name="savePcButton"
              onClick={handleSubmit_pcForm}
            >
              <b>Save</b>
            </button>
          </div>
        </ModalFooter>
      </Modal>
      {modalState_addNewContractor.showModal && (
        <EditContractorModalV2
          projectId={projectId}
          data={modalState_addNewContractor.data}
          showModal={modalState_addNewContractor.showModal}
          toggleModal={toggleModal_addNewContractor}
          listRefreshCallback={(_searchString) =>
            getPagedContractorList(_searchString || "", 1, 50, true)
          }
        />
      )}
      {showAlert_hasLinks && (
        <AlertModal
          alertTitle="Warning"
          alertTitleClass="bg-warning"
          alertTitleIcon="exclamation-triangle"
          alertText="This contractor cannot be removed from this project since it has references to one or more documents in this project."
          acceptCaption="OK"
          acceptClass="secondary"
          provideReject={false}
          handleAlertAction={toggleAlert_hasLinks}
          showAlertModal={showAlert_hasLinks}
          toggleAlertModal={toggleAlert_hasLinks}
        />
      )}
    </React.Fragment>
  );
}

function ProjContractorMultiselect_Left(props) {
  return (
    <div className="d-flex flex-column flex-fill pr-0 mr-0" style={{ width: "45%" }}>
      <div className="card m-2 flex-grow-1">
        <div className="card-body p-3">
          <h5 className="text-muted">Search/Add Contractors</h5>
          <div>{props.children}</div>
        </div>
      </div>
    </div>
  );
}

function ProjContractorMultiselect_Right(props) {
  return (
    <div className="d-flex flex-column pl-0" style={{ width: "55%" }}>
      <div className="card mr-2 my-2 edit-pc-container flex-grow-1">
        <div className="card-body p-3">
          <h5 className="text-muted">Selected Contractors for This Project</h5>
          <ul className="list-group list-group-flush lg-selectedPcList border flex-grow-1">
            {props.selectedContractors.map((pc, index) => (
              <ProjContractorMultiselect_Right_Item
                key={`selectedPcRowKey_${index}`}
                index={index}
                projectContractor={pc}
                updateIsPrime={props.updateIsPrime}
                unassignContractor={props.unassignContractor}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ProjContractorMultiselect_Right_Item(props) {
  const { projectContractor: pc, index, updateIsPrime, unassignContractor } = props;

  const handleChange_isPrime = (e) => {
    e.stopPropagation();

    updateIsPrime(index, e.target.checked);
  };

  const handleClick_unassignContractor = (e) => {
    e.preventDefault();
    e.stopPropagation();

    unassignContractor(pc.contractorId);
  };

  return (
    <li className="list-group-item">
      <div className="form-row mb-0">
        <div className="col-6 pl-0 pr-1">
          <span>{pc.name}</span>
        </div>
        <div className="col-3 px-1">
          <div className="form-group mb-0">
            <div className="custom-control custom-checkbox">
              <input
                type="checkbox"
                name="isPrime"
                id={`isPrime_${index}`}
                className="custom-control-input"
                checked={pc.isPrime}
                onChange={handleChange_isPrime}
                onClick={(e) => e.stopPropagation()}
              />
              <label
                htmlFor={`isPrime_${index}`}
                className="custom-control-label"
                onClick={(e) => e.stopPropagation()}
              >
                <b>Prime</b>
              </label>
            </div>
          </div>
        </div>
        <div className="col-3 pl-1">
          <button
            type="button"
            name={`deletePc_${index}`}
            id={`deletePc_${index}`}
            className="btn btn-sm btn-outline-secondary p-1 float-right"
            onClick={handleClick_unassignContractor}
          >
            <FontAwesomeIcon icon="times" className="fa-fw" />
          </button>
        </div>
      </div>
    </li>
  );
}
